import { message } from "antd";
import { type CheckboxChangeEvent } from "antd/lib/checkbox";
import { get, set } from "lodash-es";
import { useCallback, useEffect, useState } from "react";

import { BTCheckbox } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BdsButton, BdsText } from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";

import { externalPages } from "utilities/externalPages";
import { capitalize } from "utilities/string/string";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTFormItem, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTableCustom } from "commonComponents/btWrappers/BTTableCustom/BTTableCustom";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";

import MultiFactorAuthRoleStatus from "entity/internalUser/RolePermissions/MultiFactorAuthRoleStatus";
import {
    checkDependencies,
    getPermissionFormPath,
} from "entity/internalUser/RolePermissions/RolePermissionUtility";

import "./RolePermissions.less";

import {
    CheckAlls,
    type IPermissionEntryInfo,
    type IRolePermissionFormValues,
    isExcludedPermission,
    type Permission,
    type PermissionCategory as PermissionCategoryType,
    PermissionCheckAll,
    type PermissionSubCategory as PermissionSubCategoryType,
    type PermissionTypes,
} from "./RolePermissions.api.types";

interface ICustomPermissionProps {
    id: string;
    permission: Permission;
    disabled: boolean;
    isBelowSubCategory: boolean;
    onCheckboxChange: (type: PermissionTypes, formPath: string, checked: boolean) => void;
    checkReadonlyPermissionToast: (permission: Permission, formValueId: string) => void;
    useBds?: boolean;
}

function CustomPermission({
    id,
    permission,
    disabled,
    isBelowSubCategory,
    onCheckboxChange,
    checkReadonlyPermissionToast,
}: ICustomPermissionProps) {
    const readonly = permission.readOnly || disabled;

    const checkBox = (
        <BTCheckbox
            id={id}
            data-testid={id}
            checked={permission.enabled}
            disabled={readonly}
            onChange={(e: CheckboxChangeEvent) => {
                onCheckboxChange("custom", id, e.target.checked);
            }}
            className={cn("customPermissionCheckbox", {
                readonlyPermission: readonly,
            })}
            onClick={(e) => {
                checkReadonlyPermissionToast(permission, id);
                e.stopPropagation();
            }}
        >
            {permission.name}
        </BTCheckbox>
    );

    return (
        <BTTableCustom.BodyCell colSpan={2}>
            <BTRow align="middle">
                <BTCol>
                    <BTTitle
                        type={!permission.enabled && disabled ? "disabled" : undefined}
                        className={cn(
                            isBelowSubCategory ? "padding-left-sm" : undefined,
                            "margin-all-zero",
                        )}
                        level={4}
                    >
                        <BTPopover
                            id={id.replaceAll(`.`, `-`) + "-tooltip"}
                            content={permission.description}
                            placement="topLeft"
                            data-testid={`${id}-tooltip`}
                            widthType="medium"
                        >
                            {checkBox}
                        </BTPopover>
                    </BTTitle>
                </BTCol>
                <BTCol>
                    <PermissionEntryInfo
                        info={permission.permissionEntryInfo}
                        moreInfoLink={externalPages.XeroMFAServiceCloudHelpDoc}
                    />
                </BTCol>
            </BTRow>
        </BTTableCustom.BodyCell>
    );
}

interface IStandardPermissionProps {
    id: string;
    view?: Permission;
    add?: Permission;
    update?: Permission;
    delete?: Permission;
    disabled: boolean;
    onCheckboxChange: (type: PermissionTypes, formPath: string, checked: boolean) => void;
    checkReadonlyPermissionToast: (permission: Permission, formValueId: string) => void;
    checkViewRequiredToast: (value: string, isViewEnabled: boolean) => void;
    useBds?: boolean;
}

function StandardPermission({
    id,
    view,
    add,
    update,
    delete: deletePerm, // renamed to avoid conflict with delete keyword
    disabled,
    onCheckboxChange,
    checkReadonlyPermissionToast,
}: IStandardPermissionProps) {
    const getFormValuePath = (key: string) => {
        return `${id}.${key}`;
    };

    const getKeyDisplayString = (key: string): string => {
        if (key === "update") {
            return "Edit";
        }
        return capitalize(key);
    };

    const permissions = {
        view,
        add,
        update,
        delete: deletePerm,
    };

    if (disabled) {
        const keyIndex = 0;
        const permIndex = 1;
        const stringPerms = Object.entries(permissions)
            .filter(([_, value]) => value !== undefined)
            .filter((kvp) => kvp[permIndex] && kvp[permIndex]["enabled"])
            .map((kvp) => getKeyDisplayString(kvp[keyIndex]))
            .join(", ");

        return (
            <BTTableCustom.BodyCell>
                <BTTitle level={4} className="margin-bottom-zero">
                    {stringPerms}
                </BTTitle>
            </BTTableCustom.BodyCell>
        );
    } else {
        return (
            <BTTableCustom.BodyCell textAlign="center">
                <BTRow>
                    {Object.entries(permissions).map(([key, value]) => {
                        // Render empty column if permission doesn't exist
                        if (value === undefined) {
                            return <BTCol span={6} key={getFormValuePath(`${key}.empty`)} />;
                        }

                        const permissionPath = getFormValuePath(key);
                        const enabledPath = getFormValuePath(`${key}.enabled`);
                        const shouldDisable = value?.["readOnly"];

                        return (
                            <BTCol span={6} key={enabledPath}>
                                <BTCheckbox
                                    id={enabledPath}
                                    data-testid={enabledPath}
                                    checked={value?.["enabled"]}
                                    disabled={shouldDisable}
                                    onChange={(e: CheckboxChangeEvent) => {
                                        onCheckboxChange(
                                            key as PermissionTypes,
                                            permissionPath,
                                            e.target.checked,
                                        );
                                    }}
                                    onClick={() => {
                                        checkReadonlyPermissionToast(value, permissionPath);
                                    }}
                                />
                            </BTCol>
                        );
                    })}
                </BTRow>
            </BTTableCustom.BodyCell>
        );
    }
}

interface IPermissionEntryInfoProps {
    info?: IPermissionEntryInfo;
    moreInfoLink?: string;
}

function PermissionEntryInfo({ info, moreInfoLink }: IPermissionEntryInfoProps) {
    if (!info) {
        return null;
    }
    return (
        <div className="permissionEntryInfoContainer">
            <strong className="padding-right-xss">
                {info.featurePermissions
                    ?.map((s) => `'${s}'`)
                    .join(", ")
                    .replace(/, ([^,]*)$/, " and $1")}
            </strong>

            {info.infoText}
            {moreInfoLink && (
                <BTExternalLink className="padding-left-xs" isUnderline={false} href={moreInfoLink}>
                    Learn More
                </BTExternalLink>
            )}
        </div>
    );
}

interface IPermissionSubCategoryProps {
    id: string;
    subCategory: PermissionSubCategoryType;
    disabled: boolean;
    onCheckboxChange: (type: PermissionTypes, formPath: string, checked: boolean) => void;
    checkReadonlyPermissionToast: (permission: Permission, formValueId: string) => void;
    checkViewRequiredToast: (value: string, isViewEnabled: boolean) => void;
    useBds?: boolean;
}

function PermissionSubCategory({
    id,
    subCategory,
    disabled,
    onCheckboxChange,
    checkReadonlyPermissionToast,
    checkViewRequiredToast,
    useBds,
}: IPermissionSubCategoryProps) {
    const getFormValuePath = (key: string) => {
        return `${id}.${key}`;
    };

    const { name, permissionEntryInfo, description, permissions } = subCategory;
    const { view, add, update, delete: deletePerm, ...customPermissions } = permissions;
    const disabledTitle =
        [view, add, update, deletePerm]
            .filter((p) => p !== undefined)
            .reduce((disabled, p) => disabled && !p.enabled, true) ||
        (disabled && view && !view.enabled);

    return (
        <>
            {(view || add || update || deletePerm) && (
                <BTTableCustom.BodyRow>
                    <BTTableCustom.BodyCell className={cn({ rolePermissionEdit: !disabled })}>
                        <BTRow align="middle">
                            <BTCol>
                                <BTTitle
                                    bold
                                    level={4}
                                    type={disabledTitle ? "disabled" : undefined}
                                    className="margin-all-zero padding-right-xs"
                                >
                                    <BTPopover
                                        id={id.replaceAll(`.`, `-`) + "-tooltip"}
                                        content={description}
                                        placement="topLeft"
                                        data-testid={`${id}-tooltip`}
                                        widthType="medium"
                                    >
                                        {name}
                                    </BTPopover>
                                </BTTitle>
                            </BTCol>
                            <BTCol>
                                <PermissionEntryInfo
                                    info={permissionEntryInfo}
                                    moreInfoLink={externalPages.XeroMFAServiceCloudHelpDoc}
                                />
                            </BTCol>
                        </BTRow>
                    </BTTableCustom.BodyCell>
                    <StandardPermission
                        id={`${id}`}
                        key={`${name}StandardComponent`}
                        view={view}
                        add={add}
                        update={update}
                        delete={deletePerm}
                        disabled={disabled}
                        onCheckboxChange={onCheckboxChange}
                        checkReadonlyPermissionToast={checkReadonlyPermissionToast}
                        checkViewRequiredToast={checkViewRequiredToast}
                    />
                </BTTableCustom.BodyRow>
            )}
            {Object.entries(customPermissions).map(([key, value]) => {
                if (!isExcludedPermission(value)) {
                    return (
                        <BTTableCustom.BodyRow key={getFormValuePath(key)}>
                            <CustomPermission
                                id={getFormValuePath(key)}
                                permission={value}
                                disabled={disabled}
                                isBelowSubCategory
                                onCheckboxChange={onCheckboxChange}
                                checkReadonlyPermissionToast={checkReadonlyPermissionToast}
                                useBds={useBds}
                            />
                        </BTTableCustom.BodyRow>
                    );
                } else {
                    return null;
                }
            })}
        </>
    );
}

interface IPermissionCategoryProps {
    id: string;
    category: PermissionCategoryType;
    disabled: boolean;
    onCheckboxChange: (type: PermissionTypes, formPath: string, checked: boolean) => void;
    checkReadonlyPermissionToast: (permission: Permission, formValueId: string) => void;
    checkViewRequiredToast: (value: string, isViewEnabled: boolean) => void;
    useBds?: boolean;
}

function PermissionCategory({
    id,
    category,
    disabled,
    onCheckboxChange,
    checkReadonlyPermissionToast,
    checkViewRequiredToast,
    useBds,
}: IPermissionCategoryProps) {
    const getPermissionFormValuePath = (key: string) => {
        return `${id}.permissions.${key}`;
    };

    const getSubCategoryFormValuePath = (key: string) => {
        return `${id}.subCategories.${key}.permissions`;
    };

    const { name, subCategories, permissions } = category;

    return (
        <>
            <BTTableCustom.HeaderRow>
                <BTTableCustom.HeaderCell>
                    <BTTitle bold level={3} className="margin-all-zero">
                        {name}
                    </BTTitle>
                </BTTableCustom.HeaderCell>
                <BTTableCustom.HeaderCell />
            </BTTableCustom.HeaderRow>
            {Object.entries(subCategories).map(([key, value]) => {
                return (
                    <PermissionSubCategory
                        id={getSubCategoryFormValuePath(key)}
                        key={getSubCategoryFormValuePath(key)}
                        subCategory={value}
                        disabled={disabled}
                        onCheckboxChange={onCheckboxChange}
                        checkReadonlyPermissionToast={checkReadonlyPermissionToast}
                        checkViewRequiredToast={checkViewRequiredToast}
                        useBds={useBds}
                    />
                );
            })}
            {Object.entries(permissions).map(([key, value]) => {
                if (!isExcludedPermission(value)) {
                    return (
                        <BTTableCustom.BodyRow key={getPermissionFormValuePath(key)}>
                            <CustomPermission
                                id={getPermissionFormValuePath(key)}
                                permission={value}
                                disabled={disabled}
                                isBelowSubCategory={false}
                                onCheckboxChange={onCheckboxChange}
                                checkReadonlyPermissionToast={checkReadonlyPermissionToast}
                                useBds={useBds}
                            />
                        </BTTableCustom.BodyRow>
                    );
                } else {
                    return null;
                }
            })}
        </>
    );
}

interface IPermissionCheckAllBoxProps {
    type: PermissionTypes;
    indeterminate: boolean;
    checkAll: boolean;
    disabled: boolean;
    onMasterCheckboxChange: (
        type: PermissionTypes,
        checked: boolean,
        indeterminate: boolean,
    ) => void;
    checkViewRequiredToast: (value: string, isViewEnabled: boolean) => void;
    useBds?: boolean;
}

function PermissionCheckAllBox({
    type,
    indeterminate,
    checkAll,
    onMasterCheckboxChange,
    useBds,
}: IPermissionCheckAllBoxProps) {
    if (useBds) {
        return (
            <BTCol span={6}>
                <BTCheckbox
                    id={type}
                    data-testid={type}
                    indeterminate={indeterminate}
                    checked={checkAll}
                    onChange={(e: CheckboxChangeEvent) => {
                        onMasterCheckboxChange(type, e.target.checked, indeterminate);
                    }}
                />
            </BTCol>
        );
    }
    return (
        <BTCol span={6}>
            <BTCheckbox
                id={type}
                data-testid={type}
                indeterminate={indeterminate}
                checked={checkAll}
                onChange={(e: CheckboxChangeEvent) => {
                    onMasterCheckboxChange(type, e.target.checked, indeterminate);
                }}
            />
        </BTCol>
    );
}

interface IRolePermissionsProps {
    id: string;
    role: IRolePermissionFormValues;
    readOnly: boolean;
    readOnlyMessage?: boolean;
    hideName?: boolean;
    setFieldValue: (field: string, value: any) => void;
    setFieldTouched: (field: string, touched: boolean) => void;
    isMfaEnabled?: boolean;
    assignedUsersCount?: number;
    toggleAssignedUsersPanel?: () => void;
    showPanel?: boolean;
    handleCopyModalToggle?: () => void;
    useBds?: boolean;
}

function RolePermissions({
    id,
    role,
    readOnly,
    readOnlyMessage = true,
    hideName,
    setFieldValue,
    setFieldTouched,
    assignedUsersCount,
    toggleAssignedUsersPanel,
    showPanel,
    handleCopyModalToggle,
    useBds,
}: IRolePermissionsProps) {
    const builderInfo = useRequiredContext(BuilderInfoContext);

    const isViewCheckedInRole = useCallback((): boolean => {
        return Object.values(role.categories).some((category) =>
            Object.values(category.subCategories).some((subCategory) =>
                Object.values(subCategory.permissions).some(
                    (permission) => permission.enabled && permission.name === "View",
                ),
            ),
        );
    }, [role]);
    const isAnyViewEnabled = isViewCheckedInRole();

    const initializeCheckAlls = useCallback((): CheckAlls => {
        const initializePermissions = (newCheckAlls: CheckAlls): void => {
            for (const [categoryKey, category] of Object.entries(role.categories)) {
                for (const [subCategoryKey, subCategory] of Object.entries(
                    category.subCategories,
                )) {
                    for (const [permissionKey, permission] of Object.entries(
                        subCategory.permissions,
                    )) {
                        if (["view", "add", "update", "delete"].includes(permissionKey)) {
                            const formPath = getPermissionFormPath(
                                categoryKey,
                                subCategoryKey,
                                permissionKey,
                            );

                            addPermissionToCheckAlls(
                                newCheckAlls,
                                permissionKey,
                                formPath,
                                permission,
                            );
                        }
                    }
                }
            }
        };

        const newCheckAlls: CheckAlls = new CheckAlls();
        newCheckAlls.permissionCheckAlls["view"] = new PermissionCheckAll();
        newCheckAlls.permissionCheckAlls["add"] = new PermissionCheckAll();
        newCheckAlls.permissionCheckAlls["update"] = new PermissionCheckAll();
        newCheckAlls.permissionCheckAlls["delete"] = new PermissionCheckAll();

        initializePermissions(newCheckAlls);

        Object.values(newCheckAlls.permissionCheckAlls).forEach((permissionCheckAll) => {
            permissionCheckAll.checkAll =
                getCheckedKeyCount(permissionCheckAll) ===
                Object.entries(permissionCheckAll.permissions).length;
            permissionCheckAll.indeterminate =
                !permissionCheckAll.checkAll && getCheckedKeyCount(permissionCheckAll) !== 0;
        });

        return newCheckAlls;
    }, [role]);
    const [checkAlls, setCheckAlls] = useState<CheckAlls>(() => initializeCheckAlls());

    useEffect(() => setCheckAlls(initializeCheckAlls()), [initializeCheckAlls, role]);

    function addPermissionToCheckAlls(
        newCheckAlls: CheckAlls,
        permissionKey: string,
        formPath: string,
        permission: any,
    ): void {
        newCheckAlls.permissionCheckAlls[permissionKey].permissions[formPath] = permission;
        if (
            permission.enabled &&
            !newCheckAlls.permissionCheckAlls[permissionKey].checkedKeys.includes(formPath)
        ) {
            newCheckAlls.permissionCheckAlls[permissionKey].checkedKeys.push(formPath);
        }
    }

    function getFormValuePath(key: string): keyof IRolePermissionFormValues {
        return `${id}.${key}` as keyof IRolePermissionFormValues;
    }

    function getCategoryFormValuePath(key: string) {
        return `categories.${key}`;
    }

    function checkReadonlyPermissionToast(permission: Permission) {
        if (permission.readOnly && permission.message) {
            void message.info(permission.message, 10);
        }
    }

    function checkViewRequiredToast(value: string, isViewEnabled: boolean) {
        if (!isViewEnabled && value && ["add", "edit", "update", "delete"].includes(value)) {
            void message.info("Role must have 'view' permission to enable this permission.", 5);
        }
    }

    function getCheckedKeyCount(permissionCheckAll: PermissionCheckAll): number {
        const checkedLength = permissionCheckAll.checkedKeys.length;
        return (
            checkedLength +
            Object.entries(permissionCheckAll.permissions).filter(([key, permission]) => {
                return (
                    permission.readOnly &&
                    ((permission.enabled && !permissionCheckAll.checkedKeys.includes(key)) ||
                        (!permission.enabled && checkedLength !== 0))
                );
            }).length
        );
    }

    function handleMasterCheckboxChange(type: PermissionTypes, checked: boolean): void {
        const updatedRole = { ...role };
        const dependentPermissions: string[] = [];

        for (const [permissionFormKey, permission] of Object.entries(
            checkAlls.permissionCheckAlls[type].permissions,
        )) {
            if (!permission.readOnly) {
                set(updatedRole, `${permissionFormKey}.enabled`, checked);

                checkDependencies(
                    permissionFormKey,
                    checked,
                    updatedRole,
                    false,
                    false,
                    builderInfo.flags,
                );
            } else {
                dependentPermissions.push(permissionFormKey);
            }
        }

        // CheckDependencies can set some permissions to editable, so we need to account for those.
        for (const permissionFormKey of dependentPermissions) {
            if (!get(updatedRole, permissionFormKey).readOnly) {
                set(updatedRole, `${permissionFormKey}.enabled`, checked);
                checkDependencies(
                    permissionFormKey,
                    checked,
                    updatedRole,
                    false,
                    false,
                    builderInfo.flags,
                );
            }
        }

        setFieldValue(id, updatedRole);
        const newCheckAlls = initializeCheckAlls();
        setCheckAlls(newCheckAlls);
    }

    function handleCheckboxChange(type: PermissionTypes, formPath: string, checked: boolean): void {
        const updatedRole = { ...role };

        // This needs to be checked before setting the value due to dependencies between permissions.
        checkClientUpdatesPermissionsInfoToast(updatedRole, formPath, checked);

        set(updatedRole, `${formPath}.enabled`, checked);

        checkDependencies(formPath, checked, updatedRole, false, false, builderInfo.flags);

        setFieldValue(id, updatedRole);

        if (type !== "custom") {
            const newCheckAlls = initializeCheckAlls();
            setCheckAlls(newCheckAlls);
        }

        checkBudgetPermissionsInfoToast(formPath, checked);
    }

    function showReadOnlyMessage() {
        void message.info("To customize this standard role, you must copy from role.");
    }

    function checkBudgetPermissionsInfoToast(formPath: string, checked: boolean) {
        if (formPath.includes("categories.financial.subCategories.budgetPerms.permissions.view")) {
            if (checked) {
                const toastMessage =
                    "Granting Job Costing Budget 'view' permission automatically grants 'view' access to all other financial data.";
                void message.info(toastMessage, 5);
            } else {
                void message.info(
                    "This role no longer has 'view' access to the Job Costing Budget but can still view all other financial data.",
                    5,
                );
            }
        }
    }

    function checkClientUpdatesPermissionsInfoToast(
        formValues: IRolePermissionFormValues,
        formPath: string,
        checked: boolean,
    ) {
        const view =
            "categories.projectManagement.subCategories.clientUpdatePerms.permissions.view";
        const addUpdateDelete = [
            "categories.projectManagement.subCategories.clientUpdatePerms.permissions.add",
            "categories.projectManagement.subCategories.clientUpdatePerms.permissions.update",
            "categories.projectManagement.subCategories.clientUpdatePerms.permissions.delete",
        ];

        const currentViewValue = get(formValues, `${view}.enabled`, false);

        const toastMessage =
            "Granting 'Client Updates - View' permission automatically grants 'view' access to all related item data.";

        if (
            checked &&
            !currentViewValue &&
            addUpdateDelete.some((permissionPath) => formPath.includes(permissionPath))
        ) {
            void message.info(toastMessage, 5);
        }

        if (formPath.includes(view)) {
            if (checked) {
                void message.info(toastMessage, 5);
            } else {
                void message.info(
                    "This role no longer has 'Client Updates - View' access but can still view all other related items data.",
                    5,
                );
            }
        }
    }

    const {
        name,
        description,
        categories,
        isMfaEnabled,
        isSystemDefault,
        primaryAccessDescriptions,
        bestForDescriptions,
    } = role;
    const nameId = getFormValuePath("name");
    const descriptionId = getFormValuePath("description");

    const canSeePanelLink = !showPanel && readOnly;

    if (useBds) {
        return (
            <div className="RolePermissions">
                {!hideName && (
                    <div className="flex flex-row">
                        <div className="flex flex-col">
                            <BTFormItemAutomatic<IRolePermissionFormValues>
                                id={getFormValuePath("name")}
                                readOnly={readOnly}
                                useBds
                            >
                                <BTInput<undefined>
                                    id={nameId}
                                    data-testid={nameId}
                                    value={name}
                                    readOnly={readOnly}
                                    onChange={setFieldValue}
                                    onBlur={setFieldTouched}
                                />
                            </BTFormItemAutomatic>
                        </div>

                        {canSeePanelLink ? (
                            <div className="flex flex-col">
                                <div className="flex flex-row justify-center">
                                    <BTLink
                                        className="assigned-users-link"
                                        data-testid="assigned-users-link"
                                        to=""
                                        onClick={toggleAssignedUsersPanel}
                                        isUnderline={false}
                                    >
                                        View users
                                    </BTLink>
                                </div>
                                <div className="flex flex-row justify-center">
                                    <BdsText size="normal-sm" className="assignedUsersCount">
                                        {`${assignedUsersCount ?? 0} assigned`}
                                    </BdsText>
                                </div>
                            </div>
                        ) : (
                            !isSystemDefault &&
                            !showPanel && (
                                <div className="flex flex-col">
                                    <div className="flex flex-row">
                                        <BdsButton
                                            id="addFromRole"
                                            testid="addFromRole"
                                            value="addFromRole"
                                            displayType="secondary"
                                            text="Add from role"
                                            onClick={handleCopyModalToggle}
                                        />
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                )}
                <div className="roleDescriptionRow bds-py-4">
                    {isSystemDefault &&
                    primaryAccessDescriptions &&
                    primaryAccessDescriptions.length > 0 ? (
                        <BTFormItem label="Role description" className="fullRoleDescription">
                            <div>
                                Primary Access/Responsibilities:
                                <ul>
                                    {primaryAccessDescriptions.map((description) => (
                                        <li key={description}>{description}</li>
                                    ))}
                                </ul>
                                {bestForDescriptions && bestForDescriptions.length > 0 && (
                                    <>
                                        Best For:
                                        <ul>
                                            {bestForDescriptions.map((description) => (
                                                <li key={description}>{description}</li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                        </BTFormItem>
                    ) : (
                        <BTFormItemAutomatic
                            id={getFormValuePath("description")}
                            readOnly={readOnly}
                            useBds
                        >
                            <BTInput<undefined>
                                id={descriptionId}
                                data-testid={descriptionId}
                                value={description}
                                readOnly={readOnly}
                                onChange={setFieldValue}
                                onBlur={setFieldTouched}
                            />
                        </BTFormItemAutomatic>
                    )}
                </div>
                {role.id !== 0 && <MultiFactorAuthRoleStatus isMfaEnabled={isMfaEnabled} />}
                <div onClick={readOnly && readOnlyMessage ? showReadOnlyMessage : undefined}>
                    <BTTableCustom.Table className="RolePermissionsTable">
                        {!readOnly && (
                            <>
                                <BTTableCustom.HeaderRow className="StickyHeader">
                                    <BTTableCustom.HeaderCell />
                                    <BTTableCustom.HeaderCell customSpacing>
                                        <BTRow>
                                            <BTCol className="centerTableColumn" span={6}>
                                                View
                                            </BTCol>
                                            <BTCol className="centerTableColumn" span={6}>
                                                Add
                                            </BTCol>
                                            <BTCol className="centerTableColumn" span={6}>
                                                Edit
                                            </BTCol>
                                            <BTCol className="centerTableColumn" span={6}>
                                                Delete
                                            </BTCol>
                                        </BTRow>
                                    </BTTableCustom.HeaderCell>
                                </BTTableCustom.HeaderRow>
                                <BTTableCustom.BodyRow>
                                    <BTTableCustom.BodyCell offset={1}>
                                        <BTRow className="centerTableColumn">
                                            {Object.entries(checkAlls.permissionCheckAlls).map(
                                                ([key, value]) => {
                                                    return (
                                                        <PermissionCheckAllBox
                                                            key={key}
                                                            type={key as PermissionTypes}
                                                            indeterminate={value.indeterminate}
                                                            checkAll={value.checkAll}
                                                            disabled={
                                                                key !== "view" && !isAnyViewEnabled
                                                            }
                                                            onMasterCheckboxChange={
                                                                handleMasterCheckboxChange
                                                            }
                                                            checkViewRequiredToast={
                                                                checkViewRequiredToast
                                                            }
                                                            useBds
                                                        />
                                                    );
                                                },
                                            )}
                                        </BTRow>
                                    </BTTableCustom.BodyCell>
                                </BTTableCustom.BodyRow>
                            </>
                        )}
                        {Object.entries(categories).map(([key, value]) => {
                            return (
                                <PermissionCategory
                                    id={getCategoryFormValuePath(key)}
                                    key={getCategoryFormValuePath(key)}
                                    category={value}
                                    disabled={readOnly}
                                    onCheckboxChange={handleCheckboxChange}
                                    checkReadonlyPermissionToast={checkReadonlyPermissionToast}
                                    checkViewRequiredToast={checkViewRequiredToast}
                                    useBds
                                />
                            );
                        })}
                    </BTTableCustom.Table>
                </div>
            </div>
        );
    }

    return (
        <div className="RolePermissions">
            {!hideName && (
                <BTRow justify="space-between">
                    <BTCol span={12}>
                        <BTFormItemAutomatic<IRolePermissionFormValues>
                            id={getFormValuePath("name")}
                            readOnly={readOnly}
                        >
                            <BTInput<undefined>
                                id={nameId}
                                data-testid={nameId}
                                value={name}
                                readOnly={readOnly}
                                onChange={setFieldValue}
                                onBlur={setFieldTouched}
                            />
                        </BTFormItemAutomatic>
                    </BTCol>

                    {canSeePanelLink ? (
                        <div className="flex flex-col">
                            <div className="flex flex-row justify-center">
                                <BTLink
                                    className="assigned-users-link"
                                    data-testid="assigned-users-link"
                                    to=""
                                    onClick={toggleAssignedUsersPanel}
                                    isUnderline={false}
                                >
                                    View users
                                </BTLink>
                            </div>
                            <div className="flex flex-row justify-center">
                                <BdsText size="normal-sm" className="assignedUsersCount">
                                    {`${assignedUsersCount ?? 0} assigned`}
                                </BdsText>
                            </div>
                        </div>
                    ) : (
                        !isSystemDefault &&
                        !showPanel && (
                            <div className="flex flex-col">
                                <div className="flex flex-row">
                                    <BdsButton
                                        id="addFromRole"
                                        testid="addFromRole"
                                        value="addFromRole"
                                        displayType="secondary"
                                        text="Add from role"
                                        onClick={handleCopyModalToggle}
                                    />
                                </div>
                            </div>
                        )
                    )}
                </BTRow>
            )}
            <BTRow className="roleDescriptionRow">
                <BTCol span={12}>
                    {isSystemDefault &&
                    primaryAccessDescriptions &&
                    primaryAccessDescriptions.length > 0 ? (
                        <BTFormItem label="Role description" className="fullRoleDescription">
                            Primary Access/Responsibilities:
                            <ul>
                                {primaryAccessDescriptions.map((description) => (
                                    <li key={description}>{description}</li>
                                ))}
                            </ul>
                            {bestForDescriptions && bestForDescriptions.length > 0 && (
                                <>
                                    Best For:
                                    <ul>
                                        {bestForDescriptions.map((description) => (
                                            <li key={description}>{description}</li>
                                        ))}
                                    </ul>
                                </>
                            )}
                        </BTFormItem>
                    ) : (
                        <BTFormItemAutomatic
                            id={getFormValuePath("description")}
                            readOnly={readOnly}
                        >
                            <BTInput<undefined>
                                id={descriptionId}
                                data-testid={descriptionId}
                                value={description}
                                readOnly={readOnly}
                                onChange={setFieldValue}
                                onBlur={setFieldTouched}
                            />
                        </BTFormItemAutomatic>
                    )}
                </BTCol>
            </BTRow>
            {role.id !== 0 && (
                <BTRow>
                    <BTCol>
                        <MultiFactorAuthRoleStatus isMfaEnabled={isMfaEnabled} />
                    </BTCol>
                </BTRow>
            )}
            <div onClick={readOnly && readOnlyMessage ? showReadOnlyMessage : undefined}>
                <BTTableCustom.Table className="RolePermissionsTable">
                    {!readOnly && (
                        <>
                            <BTTableCustom.HeaderRow className="StickyHeader">
                                <BTTableCustom.HeaderCell />
                                <BTTableCustom.HeaderCell customSpacing>
                                    <BTRow>
                                        <BTCol className="centerTableColumn" span={6}>
                                            View
                                        </BTCol>
                                        <BTCol className="centerTableColumn" span={6}>
                                            Add
                                        </BTCol>
                                        <BTCol className="centerTableColumn" span={6}>
                                            Edit
                                        </BTCol>
                                        <BTCol className="centerTableColumn" span={6}>
                                            Delete
                                        </BTCol>
                                    </BTRow>
                                </BTTableCustom.HeaderCell>
                            </BTTableCustom.HeaderRow>
                            <BTTableCustom.BodyRow>
                                <BTTableCustom.BodyCell offset={1}>
                                    <BTRow className="centerTableColumn">
                                        {Object.entries(checkAlls.permissionCheckAlls).map(
                                            ([key, value]) => {
                                                return (
                                                    <PermissionCheckAllBox
                                                        key={key}
                                                        type={key as PermissionTypes}
                                                        indeterminate={value.indeterminate}
                                                        checkAll={value.checkAll}
                                                        disabled={
                                                            key !== "view" && !isAnyViewEnabled
                                                        }
                                                        onMasterCheckboxChange={
                                                            handleMasterCheckboxChange
                                                        }
                                                        checkViewRequiredToast={
                                                            checkViewRequiredToast
                                                        }
                                                    />
                                                );
                                            },
                                        )}
                                    </BTRow>
                                </BTTableCustom.BodyCell>
                            </BTTableCustom.BodyRow>
                        </>
                    )}
                    {Object.entries(categories).map(([key, value]) => {
                        return (
                            <PermissionCategory
                                id={getCategoryFormValuePath(key)}
                                key={getCategoryFormValuePath(key)}
                                category={value}
                                disabled={readOnly}
                                onCheckboxChange={handleCheckboxChange}
                                checkReadonlyPermissionToast={checkReadonlyPermissionToast}
                                checkViewRequiredToast={checkViewRequiredToast}
                            />
                        );
                    })}
                </BTTableCustom.Table>
            </div>
        </div>
    );
}

export default RolePermissions;
